export const LOGICAL_CONNECTORS = {
  AND: 'and',
  OR: 'or',
};

export const OPERATOR_DISPLAY_LABELS = {
  gt: 'Greater than',
  gte: 'Greater or equal',
  lt: 'Less than',
  lte: 'Less or equal',
  eq: 'Equals',
  neq: 'Not equal',
  contains: 'Contains',
  not_contains: 'Does not contain',
  date_after: 'Date after',
  date_before: 'Date before',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty',
};

const FIELD_DISPLAY_OVERRIDES = {
  open_po_value: 'Open PO value',
  po_status: 'PO Status',
  po_type: 'PO Type',
  vendor_name: 'Vendor Name',
  invoice_amount: 'Invoice Amount',
  total_po_value: 'Total PO value',
  gl_account: 'GL account',
};

export function formatFieldDisplayLabel(fieldValue = '') {
  const raw = String(fieldValue || '').trim();
  if (!raw) return '';
  if (FIELD_DISPLAY_OVERRIDES[raw]) return FIELD_DISPLAY_OVERRIDES[raw];
  return raw
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getOperatorDisplayLabel(operator = '', fieldType = 'text') {
  const op = String(operator || '').trim();
  if (!op) return '';
  if (op === 'eq' && fieldType === 'text') return 'Is';
  return OPERATOR_DISPLAY_LABELS[op] || op;
}

export function createDefaultConnectors(conditionCount = 0, fallback = LOGICAL_CONNECTORS.AND) {
  const count = Math.max(0, Number(conditionCount) - 1);
  return Array.from({ length: count }, () => fallback);
}

export function buildLinearPattern(connectors = [], conditionCount = 0) {
  if (!conditionCount) return '';
  if (conditionCount === 1) return '1';

  let expression = '1';
  for (let index = 0; index < connectors.length && index < conditionCount - 1; index += 1) {
    const connector = String(connectors[index] || LOGICAL_CONNECTORS.AND).toUpperCase();
    expression += ` ${connector} ${index + 2}`;
  }
  return expression;
}

export function getValuePlaceholder(fieldType = 'text') {
  if (fieldType === 'number') return 'e.g. 100,000,000';
  if (fieldType === 'text') return 'e.g. Standard, Open';
  if (fieldType === 'date') return 'Select date';
  return 'Enter value...';
}

export function formatConditionDisplayValue(fieldType = 'text', value = '') {
  if (value === null || value === undefined || value === '') return '—';
  if (fieldType === 'number') {
    const numeric = Number(String(value).replace(/,/g, ''));
    if (!Number.isNaN(numeric)) {
      return numeric.toLocaleString('en-US');
    }
  }
  return String(value);
}

export function createEmptyRuleMeta(conditionCount = 0) {
  return {
    connectors: createDefaultConnectors(conditionCount),
    useCustomPattern: false,
    customPattern: '',
  };
}

export function normalizeRuleMeta(meta = {}, conditionCount = 0) {
  const connectors = Array.isArray(meta.connectors)
    ? meta.connectors.map((item) =>
        String(item).toLowerCase() === LOGICAL_CONNECTORS.OR
          ? LOGICAL_CONNECTORS.OR
          : LOGICAL_CONNECTORS.AND,
      )
    : createDefaultConnectors(conditionCount);

  const expectedLength = Math.max(0, conditionCount - 1);
  const normalizedConnectors = connectors.slice(0, expectedLength);
  while (normalizedConnectors.length < expectedLength) {
    normalizedConnectors.push(LOGICAL_CONNECTORS.AND);
  }

  const useCustomPattern = Boolean(meta.useCustomPattern);
  const customPattern = String(meta.customPattern || '').trim()
    || buildLinearPattern(normalizedConnectors, conditionCount);

  return {
    connectors: normalizedConnectors,
    useCustomPattern,
    customPattern,
  };
}

export function getDisplayedPattern(meta = {}, conditionCount = 0) {
  const normalized = normalizeRuleMeta(meta, conditionCount);
  if (normalized.useCustomPattern && normalized.customPattern) {
    return normalized.customPattern;
  }
  return buildLinearPattern(normalized.connectors, conditionCount);
}
